/**
 * Message Screen
 * Main message interface with AI Financial Coach
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
} from 'react-native';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useConversationStore } from '../store/messageStore';
import { useProfileStore } from '../store/profileStore';
import { aiApi } from '../api/endpoints/ai';
import { financialDiagnosticApi } from '../api/endpoints/financialDiagnostic';
import { subscribeToConversation } from '../services/cable';
import { useFinancialDiagnosticStore } from '../store/financialDiagnosticStore';
import { salesApi } from '../api/endpoints/sales';
import { budgetApi } from '../api/endpoints/budget';
import MessageBubble from '../components/conversation/MessageBubble';
import PaywallModal from '../components/common/PaywallModal';
import MessageInput from '../components/conversation/MessageInput';
import RateLimitBadge from '../components/common/RateLimitBadge';
import RateLimitInlineMessage from '../components/conversation/RateLimitInlineMessage';
import ErrorMessage from '../components/common/ErrorMessage';
import Loading from '../components/common/Loading';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import AsyncStorage from '@react-native-async-storage/async-storage';
import logger from '../utils/logger';
import type { Message, GoalProposal } from '../api/types';
import { analytics, AnalyticsEvent } from '../utils/analytics';
import { STORAGE_KEYS } from '../utils/constants';
import { useChatVisibility } from '../contexts/ChatVisibilityContext';
import { useLayout } from '../components/layout/AppLayout';

interface ConversationScreenProps {
  scrollToDiagnostic?: boolean;
  triggerScanner?: boolean;
}

export default function ConversationScreen({
  scrollToDiagnostic: propScrollToDiagnostic,
  triggerScanner: propTriggerScanner,
}: ConversationScreenProps) {
  const navigation = useNavigation();
  const route = useRoute();
  const { navigateTo } = useLayout();

  const {
    conversation,
    messages,
    isLoading,
    isSending,
    error,
    rateLimitRemaining,
    rateLimitResetAt,
    setConversation,
    addMessage,
    setLoading,
    setSending,
    setError,
    setRateLimit,
    clearError,
  } = useConversationStore();

  const { hasProfile, setHasProfile } = useProfileStore();
  const { isComplete: diagnosticComplete } = useFinancialDiagnosticStore();
  const flatListRef = useRef<FlatList>(null);
  const isNearBottomRef = useRef(true); // Track if user is near bottom
  const previousMessagesLengthRef = useRef(0);
  const hasInitialScrolledRef = useRef(false); // Track if we've done initial scroll
  const contentHeightRef = useRef(0); // Track content height for scroll-to-bottom
  const layoutHeightRef = useRef(0); // Track visible layout height
  const hasTrackedFirstMessageRef = useRef(false); // Track if first message event was fired
  const lastManualScrollRef = useRef(0); // Timestamp of last manual scroll (user scrolled up)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false); // Show scroll-to-bottom button
  const [isConfirmingFinancialGoal, setIsConfirmingFinancialGoal] = useState(false);
  const [isConfirmingAllGoals, setIsConfirmingAllGoals] = useState(false);
  const [confirmingMessageId, setConfirmingMessageId] = useState<number | null>(null);
  const [confirmedMessageIds, setConfirmedMessageIds] = useState<Set<number>>(new Set());
  const [confirmedGoalsData, setConfirmedGoalsData] = useState<Map<number, any>>(new Map());
  // For multi-goal roadmaps: track confirmed goal IDs per message
  const [confirmedGoalIdsPerMessage, setConfirmedGoalIdsPerMessage] = useState<Map<number, string[]>>(new Map());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isStartingWizard, setIsStartingWizard] = useState(false);
  const [hasRecentTransactions, setHasRecentTransactions] = useState(false);
  const [showContextBanner, setShowContextBanner] = useState(false);
  const [contextBannerDismissed, setContextBannerDismissed] = useState(false);

  // Track new AI message IDs for streaming animation
  const newMessageIdsRef = useRef<Set<number>>(new Set());
  const [streamingMessageId, setStreamingMessageId] = useState<number | null>(null);

  // Load persisted confirmed message IDs on mount
  useEffect(() => {
    AsyncStorage.getItem(STORAGE_KEYS.CONFIRMED_GOAL_MESSAGE_IDS).then(data => {
      if (data) {
        try {
          const ids: number[] = JSON.parse(data);
          setConfirmedMessageIds(new Set(ids));
        } catch (_) {}
      }
    });
  }, []);

  // Paywall state for message limit
  const [showPaywall, setShowPaywall] = useState(false);

  // Handle scroll to diagnostic when coming from wizard
  const scrollToDiagnosticRef = useRef(false);
  const lastDiagnosticTimestampRef = useRef<number | null>(null);

  useEffect(() => {
    const params = route.params as { scrollToDiagnostic?: boolean; diagnosticTimestamp?: number } | undefined;
    const shouldScrollToDiagnostic = params?.scrollToDiagnostic || propScrollToDiagnostic;
    const currentTimestamp = params?.diagnosticTimestamp || Date.now();

    // Only trigger if it's a new scroll request (new timestamp)
    if (shouldScrollToDiagnostic && currentTimestamp !== lastDiagnosticTimestampRef.current) {
      logger.log('[ConversationScreen] scrollToDiagnostic triggered, reloading conversation...');
      lastDiagnosticTimestampRef.current = currentTimestamp;
      scrollToDiagnosticRef.current = true;
      // Reload conversation to get the new diagnostic message
      loadConversation();
    }
  }, [route.params, propScrollToDiagnostic]);

  // Scroll to diagnostic message when messages are loaded and scrollToDiagnostic flag is set
  useEffect(() => {
    if (scrollToDiagnosticRef.current && messages.length > 0) {
      // Find the index of the latest diagnostic message (search from end)
      const reversedMessages = [...messages].reverse();
      const reversedIndex = reversedMessages.findIndex(
        msg => msg.metadata?.diagnostic_metadata?.is_diagnostic === true
      );

      if (reversedIndex !== -1) {
        const diagnosticIndex = messages.length - 1 - reversedIndex;
        // Scroll to the diagnostic message (at the top of viewport)
        setTimeout(() => {
          flatListRef.current?.scrollToIndex({
            index: diagnosticIndex,
            animated: true,
            viewPosition: 0, // 0 = top of viewport
          });
          scrollToDiagnosticRef.current = false;
        }, 300);
      } else {
        // Fallback: scroll to end if no diagnostic found
        setTimeout(() => {
          flatListRef.current?.scrollToEnd({ animated: true });
          scrollToDiagnosticRef.current = false;
        }, 300);
      }
    }
  }, [messages]);

  // Load conversation and message history
  useEffect(() => {
    // Track message opened (with defensive check)
    if (analytics.isReady()) {
      analytics.trackEvent(AnalyticsEvent.CHAT_OPENED);
    } else {
      // Retry after initialization
      setTimeout(() => {
        analytics.trackEvent(AnalyticsEvent.CHAT_OPENED);
      }, 500);
    }

    loadConversation();
    loadUsage();

    // Check user state for toolbar actions
    checkProfileExists();
    checkRecentTransactions();
  }, []);

  // Subscribe to WebSocket for real-time messages
  useEffect(() => {
    if (!conversation?.id) return;

    let subscription: any = null;

    const connect = async () => {
      subscription = await subscribeToConversation(
        String(conversation.id),
        (message) => {
          logger.log('[Cable] New message received:', message.id);
          addMessage(message);
        }
      );
    };

    connect();

    return () => {
      if (subscription) {
        logger.log('[Cable] Unsubscribing from conversation');
        subscription.unsubscribe();
      }
    };
  }, [conversation?.id]);

  // Check if user has a financial profile
  const checkProfileExists = async () => {
    try {
      const response = await financialDiagnosticApi.getFinancialProfile();
      if (response.financial_profile) {
        setHasProfile(true);
      }
    } catch (err) {
      // Profile doesn't exist or error - hasProfile stays false
      logger.log('[ConversationScreen] No financial profile found');
    }
  };

  // Check if user has recent transactions (current month)
  const checkRecentTransactions = async () => {
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const response = await budgetApi.getTransactions({ month, per_page: 1 });
      setHasRecentTransactions(response.total_count > 0);

      // Auto-show banner if no profile and not dismissed
      if (!hasProfile && !contextBannerDismissed) {
        setShowContextBanner(true);
      }
    } catch (err) {
      logger.log('[ConversationScreen] Could not check transactions');
    }
  };

  // Reload messages when screen comes back into focus (e.g., from wizard)
  useFocusEffect(
    useCallback(() => {
      logger.log('[ConversationScreen] Screen focused - reloading messages');
      loadConversation();
    }, [])
  );

  // Hide WhatsApp FAB while this screen is visible
  const { setChatVisible } = useChatVisibility();
  useFocusEffect(
    useCallback(() => {
      setChatVisible(true);
      return () => setChatVisible(false);
    }, [setChatVisible])
  );


  // Auto-scroll to bottom ONLY when:
  // 1. New message arrives AND user was already near bottom
  // 2. Or it's the first load
  // 3. AND user hasn't manually scrolled up in the last 3 seconds (reading protection)
  useEffect(() => {
    const hasNewMessage = messages.length > previousMessagesLengthRef.current;
    const isFirstLoad = previousMessagesLengthRef.current === 0 && messages.length > 0;

    // Reading protection: don't auto-scroll if user manually scrolled up recently
    const timeSinceManualScroll = Date.now() - lastManualScrollRef.current;
    const isUserReading = timeSinceManualScroll < 3000; // 3 second cooldown

    if (isFirstLoad) {
      // Always scroll to end on first load (conversation history)
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: false });
      }, 100);
    } else if (hasNewMessage && isNearBottomRef.current && !isUserReading) {
      // New message arrived — scroll to show the AI response from the top
      const aiResponseIndex = messages.length - 1;
      setTimeout(() => {
        if (newMessageIdsRef.current.size > 0 && aiResponseIndex >= 0) {
          // Scroll so the new AI message appears at the top of the viewport
          flatListRef.current?.scrollToIndex({
            index: aiResponseIndex,
            animated: true,
            viewPosition: 0,
          });
        } else {
          flatListRef.current?.scrollToEnd({ animated: true });
        }
      }, 100);
    }

    previousMessagesLengthRef.current = messages.length;
  }, [messages.length]);

  // Track AI financial goal detection event
  const trackedGoalDetectionRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (messages.length > 0) {
      // Check if latest AI message contains financial goal detection
      const latestAiMessage = messages
        .filter(m => m.kind === 'ai_response')
        .reverse()[0];

      // Check for legacy message flow (ai_metadata.financial_goal_data)
      const hasLegacyGoal = latestAiMessage?.metadata?.ai_metadata?.financial_goal_data?.financial_goal_detected;

      // Check for diagnostic flow (goal_proposal directly in metadata)
      const hasDiagnosticGoal = latestAiMessage?.metadata?.diagnostic_metadata?.is_diagnostic &&
                                latestAiMessage?.metadata?.goal_proposal;

      if ((hasLegacyGoal || hasDiagnosticGoal) && !trackedGoalDetectionRef.current.has(latestAiMessage.id)) {
        logger.log('[Analytics] Tracking financial_goal_detected_by_ai for message:', latestAiMessage.id);
        trackedGoalDetectionRef.current.add(latestAiMessage.id);

        // Extract goal data based on flow type
        let goalCount = 0;
        let goalTypes: string[] = [];

        if (hasLegacyGoal) {
          // Legacy message flow
          goalCount = latestAiMessage.metadata?.ai_metadata?.financial_goal_data?.financial_goal_proposals?.length || 0;
          goalTypes = latestAiMessage.metadata?.ai_metadata?.financial_goal_data?.financial_goal_proposals?.map((g: any) => g.type) || [];
        } else if (hasDiagnosticGoal) {
          // Diagnostic flow
          goalCount = 1;
          goalTypes = latestAiMessage.metadata?.goal_proposal?.type ? [latestAiMessage.metadata.goal_proposal.type] : [];
        }

        analytics.trackEvent(AnalyticsEvent.FINANCIAL_GOAL_DETECTED_BY_AI, {
          financial_goal_count: goalCount,
          financial_goal_types: JSON.stringify(goalTypes),
          source: hasDiagnosticGoal ? 'diagnostic' : 'message',
        });
      }
    }
  }, [messages]);

  // Populate confirmed message IDs from backend data
  useEffect(() => {
    if (messages.length > 0) {
      const confirmed = new Set<number>();
      const confirmedData = new Map<number, any>();

      messages.forEach(msg => {
        if (msg.confirmed_goal_id) {
          confirmed.add(msg.id);
          // Store the goal ID for navigation
          confirmedData.set(msg.id, { id: msg.confirmed_goal_id });
        }
      });

      setConfirmedMessageIds(confirmed);
      setConfirmedGoalsData(confirmedData);
    }
  }, [messages]);

  const loadConversation = async () => {
    setLoading(true);
    setError(null);

    try {
      const conversationData = await aiApi.getConversation();
      setConversation(conversationData);

      // If no messages, show welcome message
      if (conversationData.messages.length === 0) {
        const welcome = await aiApi.getWelcomeMessage();
        // Welcome message will be added to conversation on next load
      }

      // Scroll to bottom after loading messages (unless we're scrolling to diagnostic)
      if (conversationData.messages.length > 0 && !scrollToDiagnosticRef.current) {
        setTimeout(() => {
          flatListRef.current?.scrollToEnd({ animated: false });
        }, 300);
      }
    } catch (err: any) {
      logger.error('Error loading conversation:', err);
      setError('Erreur lors du chargement de la conversation');
    } finally {
      setLoading(false);
    }
  };

  const loadUsage = async () => {
    try {
      const { rate_limit } = await aiApi.getUsage();
      setRateLimit(rate_limit.remaining, rate_limit.reset_at);
    } catch (err) {
      logger.error('Error loading usage:', err);
    }
  };

  const handleScroll = (event: any) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;

    // Store dimensions for scroll-to-bottom calculation
    layoutHeightRef.current = layoutMeasurement.height;
    contentHeightRef.current = contentSize.height;

    // Calculate if user is near bottom (within 100px)
    const paddingToBottom = 100;
    const isNearBottom = layoutMeasurement.height + contentOffset.y >=
                         contentSize.height - paddingToBottom;

    // Track when user manually scrolls away from bottom
    // This prevents auto-scroll from interrupting reading
    if (!isNearBottom && isNearBottomRef.current) {
      // User just scrolled up from bottom - record timestamp
      lastManualScrollRef.current = Date.now();
    }

    isNearBottomRef.current = isNearBottom;

    // Show scroll-to-bottom button when user scrolls up (away from bottom)
    setShowScrollToBottom(!isNearBottom);
  };

  const scrollToBottom = () => {
    setShowScrollToBottom(false);

    // On web: use document.getElementById to find the marker and scrollIntoView
    if (typeof document !== 'undefined') {
      const marker = document.getElementById('chat-bottom-marker');
      if (marker) {
        marker.scrollIntoView({ behavior: 'instant', block: 'start' });
        return;
      }
    }

    // Fallback: React Native scrollToEnd
    flatListRef.current?.scrollToEnd({ animated: true });
  };

  // Handle content size change - scroll to bottom on initial load
  const handleContentSizeChange = (_width: number, height: number) => {
    // Store content height for scroll-to-bottom button
    contentHeightRef.current = height;

    if (!hasInitialScrolledRef.current && height > 0 && messages.length > 0) {
      hasInitialScrolledRef.current = true;
      flatListRef.current?.scrollToEnd({ animated: false });
    }
  };

  const handleFinancialGoalConfirm = async (financialGoal: any) => {
    logger.log('[FinancialGoal] Starting confirmation:', financialGoal);
    setIsConfirmingFinancialGoal(true);
    setConfirmingMessageId(financialGoal.messageId || null);

    try {
      let response;

      // Check if this is a diagnostic goal (has source metadata)
      const isDiagnosticGoal = financialGoal.months_needed !== undefined;

      if (isDiagnosticGoal) {
        // Use diagnostic-specific endpoint
        logger.log('[FinancialGoal] Confirming diagnostic goal via confirm_diagnostic endpoint');
        response = await financialDiagnosticApi.confirmDiagnosticGoal({
          goal_proposal: {
            title: financialGoal.title,
            target_amount: financialGoal.target_amount,
            deadline: financialGoal.deadline,
            monthly_effort: financialGoal.monthly_effort,
            type: financialGoal.type,
            // Include action_steps and roadmap_data for investment goals
            action_steps: financialGoal.action_steps || [],
            roadmap_data: financialGoal.roadmap_data || {},
            current_amount: financialGoal.current_amount || 0,
          },
          message_id: financialGoal.messageId, // Link goal to message for duplicate prevention
        });
      } else {
        // Use regular endpoint
        logger.log('[FinancialGoal] Confirming regular goal via financial_goals endpoint');
        response = await aiApi.confirmFinancialGoal({
          title: financialGoal.title,
          target_amount: financialGoal.target_amount,
          deadline: financialGoal.deadline,
          monthly_effort: financialGoal.monthly_effort,
          financial_goal_type: financialGoal.type,
          message_id: financialGoal.messageId, // Link goal to message for duplicate prevention
        });
      }

      logger.log('[FinancialGoal] API response:', response);

      // Extract returned goal data with progress information
      const confirmedGoal = response.financial_goal;
      logger.log('[FinancialGoal] Confirmed goal data:', confirmedGoal);

      // Note: financial_goal_confirmed event is tracked on backend when goal is created
      // to ensure accuracy and avoid duplicate tracking

      // Clear any existing errors
      setError(null);

      // Show success message with progress info
      const progressMsg = confirmedGoal?.current_amount > 0
        ? `✅ Objectif ajouté ! Progrès initial : ${Math.round(confirmedGoal.progress_percentage)}%`
        : '✅ Objectif ajouté avec succès !';
      logger.log('[FinancialGoal] Showing success message:', progressMsg);
      setSuccessMessage(progressMsg);

      // Store confirmed goal data with progress information
      if (financialGoal.messageId && confirmedGoal) {
        setConfirmedMessageIds(prev => new Set(prev).add(financialGoal.messageId));
        setConfirmedGoalsData(prev => new Map(prev).set(financialGoal.messageId, confirmedGoal));
      }

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err: any) {
      logger.error('[FinancialGoal] Error confirming financial goal:', err);
      logger.error('[FinancialGoal] Error details:', err.response?.data);
      setError('Erreur lors de l\'ajout de l\'objectif financier');
    } finally {
      setIsConfirmingFinancialGoal(false);
      setConfirmingMessageId(null);
    }
  };

  // Handle confirming all goals at once (roadmap)
  const handleConfirmAllGoals = async (goals: GoalProposal[], messageId: number) => {
    logger.log('[FinancialGoal] Starting confirmation of all goals:', goals.length);
    setIsConfirmingAllGoals(true);
    setConfirmingMessageId(messageId);

    try {
      const response = await financialDiagnosticApi.confirmAllGoals({
        goal_proposals: goals,
        message_id: String(messageId),
      });

      logger.log('[FinancialGoal] All goals confirmed:', response);

      // Extract confirmed goal IDs - handle both roadmap and independent goals response
      let confirmedIds: string[] = [];
      if (response.is_roadmap && response.roadmap?.sub_goals) {
        // Roadmap response: extract IDs from sub_goals
        confirmedIds = response.roadmap.sub_goals.map((g: any) => g.id);
      } else if (response.financial_goals) {
        // Independent goals response
        confirmedIds = response.financial_goals.map((g: any) => g.id);
      }

      // Update state and persist to AsyncStorage
      const newConfirmedIds = new Set(confirmedMessageIds).add(messageId);
      setConfirmedMessageIds(newConfirmedIds);
      setConfirmedGoalIdsPerMessage(prev => new Map(prev).set(messageId, confirmedIds));

      // Persist confirmed IDs so they survive remounts
      AsyncStorage.setItem(
        STORAGE_KEYS.CONFIRMED_GOAL_MESSAGE_IDS,
        JSON.stringify([...newConfirmedIds]),
      ).catch(() => {});

      // Show success message
      setSuccessMessage(`🗺️ ${response.total_created} objectifs ajoutés à ta roadmap !`);

      // Auto-hide success message
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      logger.error('[FinancialGoal] Error confirming all goals:', err);
      logger.error('[FinancialGoal] Error details:', err.response?.data);
      setError('Erreur lors de l\'ajout des objectifs');
    } finally {
      setIsConfirmingAllGoals(false);
      setConfirmingMessageId(null);
    }
  };

  // Handle executing arbitrage (marking debt as repaid)
  const handleExecuteArbitrage = async (goalId: string) => {
    logger.log('[Arbitrage] Executing arbitrage for goal:', goalId);

    try {
      const response = await financialDiagnosticApi.executeArbitrage(goalId);

      logger.log('[Arbitrage] Arbitrage executed:', response);

      // Show celebration message
      setSuccessMessage(response.celebration_message || '🎉 Dette soldée !');

      // Reload conversation to reflect updated state
      await loadConversation();

      // Auto-hide success message
      setTimeout(() => setSuccessMessage(null), 8000);
    } catch (err: any) {
      logger.error('[Arbitrage] Error executing arbitrage:', err);
      setError('Erreur lors de la validation du remboursement');
    }
  };

  // Ref to prevent double-tap on wizard button
  const isNavigatingToWizardRef = useRef(false);

  const handleStartFinancialDiagnosticWizard = () => {
    // Prevent double-tap: check ref immediately (sync check)
    if (isNavigatingToWizardRef.current || isStartingWizard) {
      logger.log('[FinancialDiagnosticWizard] Already starting, ignoring duplicate tap');
      return;
    }

    // Lock immediately with ref (sync)
    isNavigatingToWizardRef.current = true;
    logger.log('[FinancialDiagnosticWizard] Starting wizard...');
    setIsStartingWizard(true);

    // Track user triggered financial diagnostic
    analytics.trackEvent(AnalyticsEvent.USER_TRIGGERED_SNAPSHOT, {
      source: 'message_action_button',
      trigger_type: 'financial_diagnostic_wizard',
    });

    try {
      // Navigate to FinancialDiagnosticWizard screen
      (navigation as any).navigate('FinancialDiagnosticWizard');
    } catch (err: any) {
      logger.error('[FinancialDiagnosticWizard] Error navigating:', err);
      setError('Impossible de démarrer le wizard');
    } finally {
      // Reset loading state after navigation attempt
      setTimeout(() => {
        setIsStartingWizard(false);
        isNavigatingToWizardRef.current = false;
      }, 1000); // Longer timeout to prevent rapid re-taps
    }
  };

  const handleViewGoal = (goalId: string) => {
    logger.log('[FinancialGoal] Navigating to goal:', goalId);
    // Navigate to Goals tab with scroll-to-goal param using layout navigation
    navigateTo('Goals', { scrollToGoalId: goalId });
  };

  const handleViewProfile = (section?: string) => {
    logger.log('[Profile] Navigating to profile, section:', section);
    // Navigate to Settings (Profile) using layout navigation
    navigateTo('Settings');
  };

  const handleOpenCsvImport = () => {
    logger.log('[CsvImport] Opening ImportBankStatementsWizard from chat');
    analytics.trackEvent(AnalyticsEvent.CHAT_OPENED, {
      source: 'chat_toolbar',
      action: 'open_csv_import',
    });
    (navigation as any).navigate('ImportBankStatementsWizard');
  };

  // Toolbar: Analyse/Importer button → smart action based on user state
  const handleAnalysePress = () => {
    if (hasRecentTransactions) {
      // Has transactions → send analysis request to AI
      logger.log('[Analyse] Sending budget analysis request');
      handleSendMessage('Analyse mon budget ce mois-ci et donne-moi des recommandations concrètes.');
    } else {
      // No transactions → open import wizard directly
      logger.log('[Analyse] No transactions, opening import wizard');
      handleOpenCsvImport();
    }
  };

  // Toolbar: Scanner de dépenses button → runs the backend scanner which formats
  // the findings via ExpenseScannerFormatter and posts them as a structured AI
  // message in the conversation. We then reload the conversation to display it.
  const [isScanning, setIsScanning] = useState(false);
  const handleScannerPress = useCallback(async () => {
    if (isScanning) return;
    logger.log('[Scanner] Running expense scan and posting to chat');
    setIsScanning(true);
    try {
      await budgetApi.runScannerAndPost();
      await loadConversation();
    } catch (err: any) {
      logger.error('[Scanner] Error running expense scan:', err);
      setError('Impossible de lancer le scanner de dépenses pour le moment.');
    } finally {
      setIsScanning(false);
    }
  }, [isScanning]);

  // Auto-trigger the scanner when navigated with triggerScanner=true
  // (e.g. right after a successful bank connection)
  const lastScannerTriggerRef = useRef<number | null>(null);
  useEffect(() => {
    const params = route.params as { triggerScanner?: boolean; scannerTimestamp?: number } | undefined;
    const shouldTrigger = params?.triggerScanner || propTriggerScanner;
    const ts = params?.scannerTimestamp || (shouldTrigger ? Date.now() : null);

    if (shouldTrigger && ts !== lastScannerTriggerRef.current) {
      lastScannerTriggerRef.current = ts;
      // Small delay to let the conversation finish loading
      setTimeout(() => {
        handleScannerPress();
      }, 600);
    }
  }, [route.params, propTriggerScanner]);

  // Contextual banner: get config based on user state
  const getContextBannerConfig = () => {
    if (!hasProfile) {
      return {
        icon: 'stethoscope',
        title: 'Diagnostic financier',
        subtitle: 'Complète ton profil pour un diagnostic personnalisé',
        actionLabel: 'Commencer',
        onAction: handleStartFinancialDiagnosticWizard,
      };
    }
    if (!hasRecentTransactions) {
      return {
        icon: 'file-document-outline',
        title: 'Importe tes relevés',
        subtitle: 'Importe un relevé CSV pour analyser tes dépenses réelles',
        actionLabel: 'Importer',
        onAction: handleOpenCsvImport,
      };
    }
    return null;
  };

  // Analyse button: label and icon adapt to user state
  const getAnalyseConfig = () => {
    if (!hasRecentTransactions) {
      return { label: 'Importer', icon: 'file-document-outline' };
    }
    return { label: 'Analyser', icon: 'chart-bar' };
  };
  const analyseConfig = getAnalyseConfig();

  // Handle diagnostic conversation actions (send_action_plan, ask_question, etc.)
  const handleDiagnosticAction = async (
    action: 'send_action_plan' | 'ask_question' | 'confirm' | 'correct',
    messageId?: string
  ) => {
    logger.log('[DiagnosticAction] Action:', action, 'messageId:', messageId);

    try {
      const response = await financialDiagnosticApi.diagnosticAction(action, {}, messageId);

      if (response.success) {
        logger.log('[DiagnosticAction] Success:', response.action);
        // Reload conversation to get the new message (action plan with goal buttons)
        await loadConversation();
      } else {
        logger.error('[DiagnosticAction] Failed');
        setError('Une erreur est survenue');
      }
    } catch (err: any) {
      logger.error('[DiagnosticAction] Error:', err);
      setError(err.response?.data?.errors || 'Une erreur est survenue');
    }
  };

  // Compute minutes remaining for rate limit countdown
  const getRateLimitMinutesLeft = useCallback(() => {
    if (!rateLimitResetAt) return 0;
    const diff = new Date(rateLimitResetAt).getTime() - Date.now();
    if (diff <= 0) return 0;
    return Math.ceil(diff / 60000);
  }, [rateLimitResetAt]);

  // Handle countdown reaching zero: reload usage to re-enable input
  const handleRateLimitCountdownEnd = useCallback(() => {
    logger.log('[ConversationScreen] Rate limit countdown ended, reloading usage');
    loadUsage();
  }, []);

  const handleSendMessage = async (messageText: string) => {
    if (rateLimitRemaining === 0) {
      // Track message limit reached event (fire and forget)
      salesApi.trackEvent('message_limit_reached')
        .then(() => {
          logger.log('[ConversationScreen] Tracked message_limit_reached');
        })
        .catch((error) => {
          logger.error('[ConversationScreen] Failed to track message_limit_reached:', error);
        });
      // Don't show paywall — the inline message handles this
      return;
    }

    // When user sends a message, they want to see it - so scroll to bottom
    isNearBottomRef.current = true;

    // Track first USER message (use ref to ensure we only track once)
    if (!hasTrackedFirstMessageRef.current) {
      hasTrackedFirstMessageRef.current = true;
      analytics.trackEvent(AnalyticsEvent.FIRST_MESSAGE_SENT, {
        message_length: messageText.length,
      });
    }

    setSending(true);
    setError(null);

    try {
      const response = await aiApi.sendMessage(messageText);

      // Add both user and AI messages
      addMessage(response.user_message);
      addMessage(response.ai_response);

      // Mark AI response for streaming animation
      if (response.ai_response?.id) {
        newMessageIdsRef.current.add(response.ai_response.id);
        setStreamingMessageId(response.ai_response.id);
      }

      // Track AI insight generated
      logger.log('[Analytics] Tracking ai_insight_generated');
      analytics.trackEvent(AnalyticsEvent.AI_INSIGHT_GENERATED, {
        response_length: response.ai_response.content?.length || 0,
        model: response.metadata?.model || 'unknown',
        tokens_used: response.metadata?.tokens?.total || 0,
      });

      // Update rate limit
      await loadUsage();
    } catch (err: any) {
      logger.error('Error sending message:', err);

      // Check for subscription required (402) or rate limit (429)
      const statusCode = err.response?.status;
      const errorType = err.response?.data?.error_type;

      if (statusCode === 402 || errorType === 'subscription_required') {
        // Show paywall for subscription required
        salesApi.trackEvent('message_limit_reached').catch(logger.error);
        setShowPaywall(true);
      } else if (statusCode === 429 || errorType === 'rate_limit_exceeded') {
        // Reload usage so inline rate limit message appears
        await loadUsage();
      } else if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        const firstError = Object.values(apiErrors)[0];
        setError(
          Array.isArray(firstError) ? firstError[0] : String(firstError)
        );
      } else {
        setError('Erreur lors de l\'envoi du message. Veuillez réessayer.');
      }
    } finally {
      setSending(false);
    }
  };

  const renderMessage = ({ item }: { item: Message }) => {
    // 'ai_message' = user sent this message to AI
    // 'ai_response' = AI's response back to user
    const isUser = item.kind === 'ai_message';
    const isConfirmed = confirmedMessageIds.has(item.id);
    const confirmedGoalData = confirmedGoalsData.get(item.id);

    // Get confirmed goal ID from backend data (item.confirmed_goal_id) or from local state
    const confirmedGoalId = item.confirmed_goal_id || confirmedGoalData?.id;

    // For multi-goal roadmaps: get array of confirmed goal IDs
    const confirmedGoalIds = confirmedGoalIdsPerMessage.get(item.id) ||
                             (item.confirmed_goal_ids ? item.confirmed_goal_ids : []);

    // If confirmed, merge the goal data with progress info into the message
    const messageWithProgress: Message = isConfirmed && confirmedGoalData
      ? {
          ...item,
          metadata: {
            ...item.metadata,
            goal_proposal: {
              ...item.metadata?.goal_proposal,
              current_amount: confirmedGoalData.current_amount,
              progress_percentage: confirmedGoalData.progress_percentage,
              amount_remaining: confirmedGoalData.amount_remaining,
              is_goal_reached: confirmedGoalData.is_goal_reached,
            } as any,
          },
        }
      : item;

    return (
      <MessageBubble
        message={messageWithProgress}
        isUser={isUser}
        onFinancialGoalConfirm={handleFinancialGoalConfirm}
        onConfirmAllGoals={handleConfirmAllGoals}
        onExecuteArbitrage={handleExecuteArbitrage}
        onStartFinancialDiagnosticWizard={handleStartFinancialDiagnosticWizard}
        onViewGoal={handleViewGoal}
        onViewProfile={handleViewProfile}
        onDiagnosticAction={handleDiagnosticAction}
        onOpenCsvImport={handleOpenCsvImport}
        isConfirmingFinancialGoal={isConfirmingFinancialGoal && confirmingMessageId === item.id}
        isConfirmingAllGoals={isConfirmingAllGoals && confirmingMessageId === item.id}
        isGoalConfirmed={isConfirmed}
        confirmedGoalId={confirmedGoalId}
        confirmedGoalIds={confirmedGoalIds}
        isStartingWizard={isStartingWizard}
        isNewMessage={!isUser && newMessageIdsRef.current.has(item.id)}
        onStreamingComplete={() => {
          newMessageIdsRef.current.delete(item.id);
          setStreamingMessageId(null);
        }}
      />
    );
  };

  if (isLoading && !conversation) {
    return (
      <View style={styles.container}>
        <Loading fullScreen message="Chargement de la conversation..." />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Compact header - only show rate limit badge when relevant */}
      {rateLimitRemaining < 20 && (
        <View style={styles.headerCompact}>
          <RateLimitBadge
            remaining={rateLimitRemaining}
            showWarning={rateLimitRemaining < 10}
          />
        </View>
      )}

      <ErrorMessage error={error} onDismiss={clearError} />

      {successMessage && (
        <View style={styles.successMessage}>
          <Text style={styles.successMessageText}>{successMessage}</Text>
          <TouchableOpacity onPress={() => setSuccessMessage(null)}>
            <Text style={styles.successMessageClose}>✕</Text>
          </TouchableOpacity>
        </View>
      )}

      <FlatList
        ref={flatListRef}
        style={styles.flatList}
        data={messages.filter(msg => msg && msg.id)}
        renderItem={renderMessage}
        keyExtractor={(item) => item.id ? item.id.toString() : `temp-${Date.now()}`}
        contentContainerStyle={styles.messagesContainer}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        onLayout={(event) => {
          layoutHeightRef.current = event.nativeEvent.layout.height;
        }}
        scrollEventThrottle={16}
        onScrollToIndexFailed={(info) => {
          // Retry scrolling after a delay when the item hasn't been rendered yet
          setTimeout(() => {
            flatListRef.current?.scrollToIndex({
              index: info.index,
              animated: true,
              viewPosition: 0,
            });
          }, 500);
        }}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              Commencez une conversation avec votre assistant financier AI
            </Text>
          </View>
        }
        ListFooterComponent={
          <>
            {rateLimitRemaining === 0 && (
              <RateLimitInlineMessage
                resetAt={rateLimitResetAt}
                onUpgradePress={() => setShowPaywall(true)}
                onCountdownEnd={handleRateLimitCountdownEnd}
              />
            )}
            <View nativeID="chat-bottom-marker" style={{ height: 1 }} />
          </>
        }
      />

      {isSending && (
        <View style={styles.sendingIndicator}>
          <Text style={styles.sendingText}>Wealfi écrit...</Text>
        </View>
      )}

      {/* Scroll to bottom button */}
      {showScrollToBottom && (
        <View style={styles.scrollToBottomContainer}>
          <TouchableOpacity
            style={styles.scrollToBottomButton}
            onPress={scrollToBottom}
            activeOpacity={0.8}
          >
            <Icon name="chevron-down" size={24} color={colors.background} />
          </TouchableOpacity>
        </View>
      )}

      {/* Contextual banner - shows only when relevant */}
      {showContextBanner && !contextBannerDismissed && (() => {
        const bannerConfig = getContextBannerConfig();
        if (!bannerConfig) return null;
        return (
          <View style={styles.contextBanner}>
            <View style={styles.contextBannerContent}>
              <Icon name={bannerConfig.icon} size={18} color={colors.primary} />
              <View style={styles.contextBannerText}>
                <Text style={styles.contextBannerTitle}>{bannerConfig.title}</Text>
                <Text style={styles.contextBannerSubtitle}>{bannerConfig.subtitle}</Text>
              </View>
              <TouchableOpacity
                style={styles.contextBannerAction}
                onPress={bannerConfig.onAction}
              >
                <Text style={styles.contextBannerActionText}>{bannerConfig.actionLabel}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  setShowContextBanner(false);
                  setContextBannerDismissed(true);
                }}
                style={styles.contextBannerDismiss}
              >
                <Icon name="close" size={16} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
          </View>
        );
      })()}

      <MessageInput
        onSend={handleSendMessage}
        onDiagnosticPress={handleStartFinancialDiagnosticWizard}
        onAnalysePress={handleAnalysePress}
        analyseLabel={analyseConfig.label}
        analyseIcon={analyseConfig.icon}
        onScannerPress={handleScannerPress}
        disabled={rateLimitRemaining === 0}
        loading={isSending || isScanning}
        placeholder={
          rateLimitRemaining === 0
            ? `Disponible dans ${getRateLimitMinutesLeft() || 1} min...`
            : undefined
        }
      />

      {/* Paywall Modal for message limit */}
      <PaywallModal
        visible={showPaywall}
        onClose={() => setShowPaywall(false)}
        onSubscribeSuccess={() => {
          setShowPaywall(false);
          // Reload usage to get new limits after subscription
          loadUsage();
        }}
        trigger="limit"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    // @ts-ignore - overflow and height work on web
    overflow: 'hidden',
    height: '100%',
  },
  headerCompact: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  successMessage: {
    backgroundColor: '#E8F5E9',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  successMessageText: {
    fontSize: typography.fontSize.sm,
    color: '#2E7D32',
    flex: 1,
  },
  successMessageClose: {
    fontSize: typography.fontSize.lg,
    color: '#2E7D32',
    paddingHorizontal: spacing.sm,
  },
  flatList: {
    flex: 1,
  },
  messagesContainer: {
    paddingVertical: spacing.md,
    flexGrow: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  sendingIndicator: {
    padding: spacing.sm,
    alignItems: 'center',
  },
  sendingText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
  scrollToBottomContainer: {
    position: 'absolute',
    bottom: 80, // Above MessageInput
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 1000,
    pointerEvents: 'box-none',
  },
  scrollToBottomButton: {
    backgroundColor: colors.primary,
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  // Contextual banner styles (above input)
  contextBanner: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  contextBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '0D',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.primary + '30',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    gap: spacing.sm,
  },
  contextBannerText: {
    flex: 1,
  },
  contextBannerTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  contextBannerSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 1,
  },
  contextBannerAction: {
    backgroundColor: colors.primary,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 16,
  },
  contextBannerActionText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },
  contextBannerDismiss: {
    padding: 2,
  },
});

